import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type {
  OrgRole,
  UserDto,
} from '@antigravity/types';
import { apiClient } from '../services/api-client';

export interface UserOrg {
  id: string;
  name: string;
  slug: string;
  role: OrgRole;
}

interface AuthContextType {
  user: UserDto | null;
  organizations: UserOrg[];
  currentOrg: UserOrg | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  switchOrganization: (orgId: string) => void;
  createOrganization: (name: string, slug?: string) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [organizations, setOrganizations] = useState<UserOrg[]>([]);
  const [currentOrg, setCurrentOrg] = useState<UserOrg | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const initAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.getMe();
      setUser(data.user);
      setOrganizations(data.organizations as UserOrg[]);

      // Select active organization (saved in session or first available)
      const savedOrgId = sessionStorage.getItem('ag_active_org_id');
      const targetOrg =
        data.organizations.find((o) => o.id === savedOrgId) ||
        data.organizations[0];

      if (targetOrg) {
        setCurrentOrg(targetOrg as UserOrg);
        apiClient.setCurrentOrganizationId(targetOrg.id);
      }
    } catch {
      setUser(null);
      setOrganizations([]);
      setCurrentOrg(null);
      apiClient.setAccessToken(null);
      apiClient.setCurrentOrganizationId(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = async (email: string, password: string) => {
    const session = await apiClient.login({ email, password });
    setUser(session.user);
    setOrganizations(session.organizations as UserOrg[]);
    if (session.organizations.length > 0) {
      const firstOrg = session.organizations[0] as UserOrg;
      setCurrentOrg(firstOrg);
      apiClient.setCurrentOrganizationId(firstOrg.id);
      sessionStorage.setItem('ag_active_org_id', firstOrg.id);
    }
  };

  const register = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ) => {
    const session = await apiClient.register({
      firstName,
      lastName,
      email,
      password,
    });
    setUser(session.user);
    setOrganizations(session.organizations as UserOrg[]);
    if (session.organizations.length > 0) {
      const firstOrg = session.organizations[0] as UserOrg;
      setCurrentOrg(firstOrg);
      apiClient.setCurrentOrganizationId(firstOrg.id);
      sessionStorage.setItem('ag_active_org_id', firstOrg.id);
    }
  };

  const logout = async () => {
    try {
      await apiClient.logout();
    } finally {
      setUser(null);
      setOrganizations([]);
      setCurrentOrg(null);
      sessionStorage.removeItem('ag_active_org_id');
      apiClient.setCurrentOrganizationId(null);
      apiClient.setAccessToken(null);
    }
  };

  const switchOrganization = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    if (org) {
      setCurrentOrg(org);
      apiClient.setCurrentOrganizationId(org.id);
      sessionStorage.setItem('ag_active_org_id', org.id);
    }
  };

  const createOrganization = async (name: string, slug?: string) => {
    const newOrg = await apiClient.createOrganization({ name, slug });
    const userOrg: UserOrg = {
      id: newOrg.id,
      name: newOrg.name,
      slug: newOrg.slug,
      role: 'OWNER',
    };
    setOrganizations((prev) => [...prev, userOrg]);
    setCurrentOrg(userOrg);
    apiClient.setCurrentOrganizationId(userOrg.id);
    sessionStorage.setItem('ag_active_org_id', userOrg.id);
  };

  const refreshUserProfile = async () => {
    await initAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organizations,
        currentOrg,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        switchOrganization,
        createOrganization,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
